/**
 * AsyncBatchProcessor - Batch Processing with Cancellation & Time-Based Yielding
 * 
 * This module provides batch processing utilities with:
 * - CancellationToken-based cancellation
 * - Time-based yielding (yieldInterval) as alternative to count-based (batchSize)
 * - Structured result objects with cancellation state
 * - Progress events with timing information
 * - Error handling strategies
 * - Generator-based alternative for native break support
 * - Full backward compatibility with existing API
 * 
 * ## Yield Strategies
 * 
 * **Count-based (batchSize):** Yields after N items. Simple but doesn't adapt
 * to varying item processing times.
 * 
 * **Time-based (yieldInterval):** Yields approximately every N milliseconds.
 * Adapts to workload, provides consistent UI responsiveness regardless of
 * item processing duration.
 * 
 * @module AsyncBatchProcessor
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Cancellation token interface.
 * Declared locally to avoid dependency on @cic/authoring.
 * Compatible with ICancellationToken from @cic/authoring.
 */
export interface ICancellationToken {
    readonly isCancellationRequested: boolean;
}

/**
 * Progress event emitted during batch processing
 */
export interface BatchProgressEvent {
    /** Number of items processed so far */
    processedCount: number;
    /** Total number of items to process */
    totalCount: number;
    /** Current batch/yield number (1-indexed) */
    batchNumber: number;
    /** 
     * Total number of batches (only available for count-based batching).
     * Undefined for time-based yielding since batch count isn't predictable.
     */
    totalBatches?: number;
    /** Elapsed time in milliseconds */
    elapsedMs: number;
    /** Estimated remaining time in milliseconds */
    estimatedRemainingMs?: number;
}

/**
 * Result object returned from batch processing with cancellation support
 */
export interface BatchProcessResult<R> {
    /** Results collected during processing */
    results: R[];
    /** Whether processing was cancelled via the cancellation token */
    cancelled: boolean;
    /** Number of items successfully processed */
    processedCount: number;
    /** Total number of items in the input array */
    totalCount: number;
    /** Errors collected during processing (when using 'collect' error strategy) */
    errors?: Array<{ error: Error; item: unknown; index: number }>;
    /** Total elapsed time in milliseconds */
    elapsedMs: number;
}

/**
 * Error handling strategies for batch processing
 */
export type ErrorStrategy = 
    | 'throw'    // Stop on first error, rethrow (default)
    | 'skip'     // Skip failed items, continue processing
    | 'collect'; // Collect all errors, return at end

/**
 * Options for batch processing
 */
export interface BatchProcessOptions<T, R> {
    /**
     * The array of items to process
     */
    items: T[];
    
    /**
     * The async function to process each item
     */
    processor: (item: T, index: number) => Promise<R>;
    
    // ─── Yield Strategy (choose one) ───────────────────────────────────────
    
    /**
     * Number of items to process before yielding to the event loop.
     * Ignored if yieldInterval is specified.
     * @default 100
     */
    batchSize?: number;
    
    /**
     * Yield approximately every N milliseconds.
     * When set, batchSize is ignored.
     * 
     * Recommended values:
     * - 8-16ms: Smooth UI updates (60fps)
     * - 50-100ms: Background processing
     * - 100-200ms: Heavy computation with minimal overhead
     * 
     * @example
     * ```typescript
     * // Yield every ~16ms to maintain 60fps
     * { yieldInterval: 16 }
     * ```
     */
    yieldInterval?: number;
    
    // ─── Callbacks ─────────────────────────────────────────────────────────
    
    /**
     * Optional callback invoked after each yield point.
     * Useful for progress tracking.
     */
    onBatchComplete?: (processedCount: number, totalCount: number) => Promise<void>;
    
    /**
     * Progress callback with detailed timing and batch information
     */
    onProgress?: (event: BatchProgressEvent) => void;
    
    // ─── Cancellation ──────────────────────────────────────────────────────
    
    /**
     * Cancellation token to stop batch processing.
     * When isCancellationRequested becomes true, processing stops
     * and returns partial results with cancelled: true.
     */
    cancellationToken?: ICancellationToken;
    
    // ─── Error Handling ────────────────────────────────────────────────────
    
    /**
     * How to handle errors during processing
     * @default 'throw'
     */
    errorStrategy?: ErrorStrategy;
}

// ============================================================================
// Convenience Presets
// ============================================================================

/**
 * Recommended yield intervals for common use cases
 */
export const YieldPresets = {
    /** Smooth 60fps UI updates (leaves ~8ms for render) */
    SMOOTH_UI: 8,
    /** Standard 60fps frame timing */
    FRAME_60FPS: 16,
    /** 30fps for less critical updates */
    FRAME_30FPS: 33,
    /** Background processing, less frequent yields */
    BACKGROUND: 100,
    /** Heavy computation, minimize overhead */
    HEAVY_COMPUTE: 200
} as const;

// ============================================================================
// Core Implementation
// ============================================================================

/**
 * Process an array of items with cancellation and configurable yield strategy.
 * 
 * Yields to the event loop periodically to keep the UI responsive.
 * Supports both count-based (batchSize) and time-based (yieldInterval) yielding.
 * 
 * @example Basic usage with cancellation and time-based yielding
 * ```typescript
 * const { results, cancelled } = await processBatchesWithCancellation({
 *     items: largeArray,
 *     processor: async (item) => await processItem(item),
 *     yieldInterval: 16,  // Yield every ~16ms for 60fps
 *     cancellationToken,
 *     onProgress: ({ processedCount, totalCount }) => {
 *         progressBar.setValue(processedCount / totalCount);
 *     }
 * });
 * 
 * if (cancelled) {
 *     return getCancelledCommandResponse();
 * }
 * ```
 * 
 * @example Count-based batching (backward compatible)
 * ```typescript
 * const { results } = await processBatchesWithCancellation({
 *     items: entities,
 *     processor: async (entity) => await transform(entity),
 *     batchSize: 100,
 *     cancellationToken
 * });
 * ```
 * 
 * @example With error collection
 * ```typescript
 * const { results, errors } = await processBatchesWithCancellation({
 *     items: entities,
 *     processor: async (entity) => await riskyOperation(entity),
 *     yieldInterval: 50,
 *     errorStrategy: 'collect'
 * });
 * 
 * if (errors?.length) {
 *     console.warn(`${errors.length} items failed`);
 * }
 * ```
 */
export async function processBatchesWithCancellation<T, R>(
    options: BatchProcessOptions<T, R>
): Promise<BatchProcessResult<R>> {
    const { 
        items, 
        processor, 
        batchSize = 100,
        yieldInterval,
        onBatchComplete,
        onProgress,
        cancellationToken,
        errorStrategy = 'throw'
    } = options;
    
    const results: R[] = [];
    const errors: Array<{ error: Error; item: unknown; index: number }> = [];
    const totalCount = items.length;
    const startTime = performance.now();
    
    // Determine yield strategy
    const useTimeBasedYield = yieldInterval !== undefined && yieldInterval > 0;
    const totalBatches = useTimeBasedYield ? undefined : Math.ceil(totalCount / batchSize);
    
    let processedCount = 0;
    let batchNumber = 0;
    let lastYieldTime = performance.now();
    
    /**
     * Check if cancellation has been requested
     */
    const isCancelled = (): boolean => {
        return cancellationToken?.isCancellationRequested === true;
    };
    
    /**
     * Determine if we should yield to the event loop
     */
    const shouldYield = (): boolean => {
        if (useTimeBasedYield) {
            return performance.now() - lastYieldTime >= yieldInterval;
        }
        // Count-based: yield after every batchSize items
        return processedCount > 0 && processedCount % batchSize === 0;
    };
    
    /**
     * Create result object
     */
    const createResult = (cancelled: boolean): BatchProcessResult<R> => ({
        results,
        cancelled,
        processedCount,
        totalCount,
        errors: errors.length > 0 ? errors : undefined,
        elapsedMs: performance.now() - startTime
    });
    
    /**
     * Emit progress event
     */
    const emitProgress = (): void => {
        if (!onProgress) return;
        
        const elapsedMs = performance.now() - startTime;
        const itemsPerMs = processedCount / elapsedMs;
        const remainingItems = totalCount - processedCount;
        const estimatedRemainingMs = itemsPerMs > 0 
            ? remainingItems / itemsPerMs 
            : undefined;
        
        onProgress({
            processedCount,
            totalCount,
            batchNumber,
            totalBatches,
            elapsedMs,
            estimatedRemainingMs
        });
    };
    
    /**
     * Perform yield to event loop with callbacks
     */
    const performYield = async (): Promise<void> => {
        batchNumber++;
        
        emitProgress();
        
        // Legacy callback
        if (onBatchComplete) {
            await onBatchComplete(processedCount, totalCount);
        }
        
        // Yield to event loop
        await yieldToEventLoop();
        lastYieldTime = performance.now();
    };
    
    /**
     * Handle error based on strategy
     */
    const handleError = (error: unknown, item: T, index: number): void => {
        switch (errorStrategy) {
            case 'throw':
                throw error;
            case 'skip':
                // Just continue, don't add to results
                break;
            case 'collect':
                errors.push({ 
                    error: error as Error, 
                    item, 
                    index 
                });
                break;
        }
    };
    
    // ─── Main Processing Loop ──────────────────────────────────────────────
    
    for (let i = 0; i < totalCount; i++) {
        // Check for cancellation before each item
        if (isCancelled()) {
            return createResult(true);
        }
        
        // Process item
        try {
            const result = await processor(items[i], i);
            results.push(result);
        } catch (error) {
            handleError(error, items[i], i);
        }
        
        processedCount++;
        
        // Yield if needed (and not last item)
        if (shouldYield() && processedCount < totalCount) {
            await performYield();
        }
    }
    
    // Final progress emit
    if (processedCount > 0) {
        batchNumber++;
        emitProgress();
    }
    
    return createResult(false);
}

/**
 * Process items with cancellation support, filtering out null/undefined results.
 * 
 * Useful when some items may not produce results (e.g., conditional processing).
 * 
 * @example
 * ```typescript
 * const { results } = await processBatchesFilteredWithCancellation({
 *     items: entities,
 *     processor: async (entity) => {
 *         if (shouldSkip(entity)) return null;
 *         return processEntity(entity);
 *     },
 *     yieldInterval: 16,
 *     cancellationToken
 * });
 * // results contains only non-null values
 * ```
 */
export async function processBatchesFilteredWithCancellation<T, R>(
    options: BatchProcessOptions<T, R | null | undefined>
): Promise<BatchProcessResult<R>> {
    const result = await processBatchesWithCancellation(options);
    return {
        ...result,
        results: result.results.filter((r): r is R => r !== null && r !== undefined)
    };
}

// ============================================================================
// Generator-Based Alternative
// ============================================================================

/**
 * Process items as an async generator, yielding results one at a time.
 * 
 * Benefits:
 * - Native `break` support via for-await-of
 * - Memory efficient for very large datasets (doesn't accumulate results)
 * - Lazy evaluation
 * 
 * Supports both count-based and time-based yielding.
 * 
 * @example With native break
 * ```typescript
 * const processedItems: ProcessedEntity[] = [];
 * 
 * for await (const result of processBatchesIterator({
 *     items: largeDataset,
 *     processor: async (item) => transformItem(item),
 *     yieldInterval: 16,
 *     cancellationToken
 * })) {
 *     if (someOtherCondition) {
 *         break; // Native break support!
 *     }
 *     processedItems.push(result);
 * }
 * ```
 */
export async function* processBatchesIterator<T, R>(
    options: Omit<BatchProcessOptions<T, R>, 'errorStrategy' | 'onProgress'>
): AsyncGenerator<R, void, unknown> {
    const { 
        items, 
        processor, 
        batchSize = 100,
        yieldInterval,
        cancellationToken,
        onBatchComplete 
    } = options;
    
    const totalCount = items.length;
    const useTimeBasedYield = yieldInterval !== undefined && yieldInterval > 0;
    
    let processedCount = 0;
    let lastYieldTime = performance.now();
    
    const shouldYield = (): boolean => {
        if (useTimeBasedYield) {
            return performance.now() - lastYieldTime >= yieldInterval;
        }
        return processedCount > 0 && processedCount % batchSize === 0;
    };
    
    for (let i = 0; i < totalCount; i++) {
        // Check for cancellation
        if (cancellationToken?.isCancellationRequested) {
            return;
        }
        
        yield await processor(items[i], i);
        processedCount++;
        
        // Yield to event loop if needed
        if (shouldYield() && processedCount < totalCount) {
            if (onBatchComplete) {
                await onBatchComplete(processedCount, totalCount);
            }
            await yieldToEventLoop();
            lastYieldTime = performance.now();
        }
    }
    
    // Final callback
    if (onBatchComplete && totalCount > 0) {
        await onBatchComplete(totalCount, totalCount);
    }
}

// ============================================================================
// Backward-Compatible Wrappers
// ============================================================================

/**
 * Process an array of items in batches, yielding to the event loop between batches
 * to keep the UI responsive and allow for command cancellation.
 * 
 * @example
 * ```typescript
 * const results = await processBatches({
 *   items: largeArray,
 *   processor: async (item) => await processItem(item),
 *   batchSize: 100,
 *   onBatchComplete: (processed, total) => console.log(`${processed}/${total}`)
 * });
 * ```
 */
export async function processBatches<T, R>(
    options: Omit<BatchProcessOptions<T, R>, 'cancellationToken' | 'errorStrategy' | 'onProgress' | 'yieldInterval'>
): Promise<R[]> {
    const result = await processBatchesWithCancellation(options);
    return result.results;
}

/**
 * Similar to processBatches, but filters out undefined/null results automatically.
 * Useful when some items may not produce results.
 */
export async function processBatchesFiltered<T, R>(
    options: Omit<BatchProcessOptions<T, R | null | undefined>, 'cancellationToken' | 'errorStrategy' | 'onProgress' | 'yieldInterval'>
): Promise<R[]> {
    const result = await processBatchesFilteredWithCancellation(options);
    return result.results;
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Yields control back to the event loop to keep the UI responsive.
 * Uses a combination of setTimeout and requestAnimationFrame for optimal behavior.
 * 
 * The setTimeout(0) ensures we yield to the macrotask queue, allowing:
 * - Pending UI updates to render
 * - User interactions to be processed
 * - Other scheduled tasks to run
 * 
 * The requestAnimationFrame ensures the browser has painted before we resume,
 * which is important for smooth progress bar updates.
 */
async function yieldToEventLoop(): Promise<void> {
    return new Promise<void>((resolve) => {
        // Use setTimeout with 0 delay to yield to the event loop
        // This allows other pending tasks (like UI updates, user interactions) to execute
        setTimeout(() => {
            // Also request an animation frame to ensure the browser has a chance to render
            if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => resolve());
            } else {
                resolve();
            }
        }, 0);
    });
}
